import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {projectStatus} from 'constants/enums';

export type employeeAssignedProjects = {
  end_date?: Date;
  status?: keyof typeof projectStatus;
  name: string;
  id: number;
};
export type Employee = {
  id: number;
  emp_id: number;
  org_id?: number;
  is_active?: boolean;
  first_name?: string;
  last_name?: string;
  email?: string;
  personal_email?: string;
  phone?: string;
  dob?: Date;
  date_of_joining?: Date;
  project?: employeeAssignedProjects[];
  profile_photo_url?: string;
  last_day_of_work?: Date | null;
};

export type EmployeeStateType = {
  isLoading: boolean;
  errorEmployeeState: boolean | string;
  successEmployeeState: boolean | string;
  totalEmployeePages: number;
  currentEmployeePage: number;
  employeesList: Employee[];
  allEmployees: Employee[];
  nextEmployeePage: number | null;
  selectedEmployeeData: {
    id: number | null;
    data: Employee | null;
  };
};

const initialState: EmployeeStateType = {
  isLoading: false,
  errorEmployeeState: false,
  successEmployeeState: false,
  totalEmployeePages: 0,
  currentEmployeePage: 0,
  employeesList: [],
  allEmployees:[],
  nextEmployeePage: null,
  selectedEmployeeData: {
    data: null,
    id: null,
  },
};
/**
 * get employees
 */
export type getEmployeesRequestParams = {
  org_id: number;
  page?: number;
  search_text?: string;
  sort?: string;
  filter?: any[];
};

export type getEmployeesSuccessResponse = {
  data: {
    employees: Employee[];
    current_page: number;
    next_page: number | null;
    total_page: number;
  };
  status_code: string;
};

export type addEmployeeRequestPayload = Omit<
  Employee,
  'id' | 'project' | 'dob' | 'date_of_joining' // Ommiting some field to redefine then with different data type
> & {project: number[]; dob: string; date_of_joining: string};

export type addEmployeeSucessResponse = {
  data: Employee;
  status_code: string;
};

/**
 * get employee details by id
 */

export type getEmployeeDetailsParam = {
  id: number;
};

export type getEmployeeDetailsSuccessResponse = {
  data: Omit<Employee, 'org_id' | 'email' | 'personal_email' | 'phone'>;
  status_code: string;
};

/**
 * update employee details
 */

export type updateEmployeeDetailsParams = Omit<
  Employee,
  'emp_id' | 'org_id' | 'email' | 'personal_email' | 'phone' | 'project' // Ommiting some field to redefine then with different data type
> & {dob: string; date_of_joining: string; last_day_of_work: string};

export type updateEmployeeDetailsSuccessResponse = {
  status_code: string;
  data: Employee;
};

const EmployeeSlice = createSlice({
  name: 'empoyees',
  initialState,
  reducers: {
    getEmployeesRequest(
      state,
      action: PayloadAction<getEmployeesRequestParams>,
    ) {
      state.isLoading = true;
      state.errorEmployeeState = false;
      // this will be empty everty time we try to fetch data from add employee and add project page
      (action.payload?.page === 1) && (state.allEmployees = []); 

    },
    getEmployeesSuccess(
      state,
      action: PayloadAction<getEmployeesSuccessResponse>,
    ) {
      state.isLoading = false;
      state.currentEmployeePage = action.payload.data.current_page;
      state.totalEmployeePages = action.payload.data.total_page;
      state.nextEmployeePage = action.payload.data.next_page;
      state.employeesList = action.payload.data.employees;

      // keep concatinating the list at the end, the filter 
      // data will not be added into this since those dropdown 
      // are on the other screen and on those screen data will be 
      // fetched from beggining
      if (action.payload.data.current_page == 1) {
        state.allEmployees = action.payload.data.employees;
      } else {
        let allEmployees = [...state.allEmployees];
        allEmployees = allEmployees.concat(action.payload.data.employees);
        state.allEmployees = allEmployees;
      }
    },
    getEmployeesFailure(state, action: PayloadAction<string | boolean>) {
      state.errorEmployeeState = action.payload;
      state.isLoading = false;
    },
    addEmployeeRequest(
      state,
      action: PayloadAction<addEmployeeRequestPayload>,
    ) {
      state.isLoading = true;
      state.errorEmployeeState = false;
    },
    addEmployeeSuccess(
      state,
      action: PayloadAction<addEmployeeSucessResponse>,
    ) {
      state.isLoading = false;
      state.successEmployeeState = action.payload.status_code;
    },
    addEmployeeFailure(state, action: PayloadAction<string | boolean>) {
      state.isLoading = false;
      state.errorEmployeeState = action.payload;
    },
    setCurrentSelectedEmployee(state, action: PayloadAction<number>) {
      state.selectedEmployeeData.id = action.payload;
    },
    getEmployeeDetailsRequest(
      state,
      action: PayloadAction<getEmployeeDetailsParam>,
    ) {
      state.isLoading = true;
      state.errorEmployeeState = false;
    },
    getEmployeeDetailsSuccess(
      state,
      action: PayloadAction<getEmployeeDetailsSuccessResponse>,
    ) {
      state.isLoading = false;
      state.selectedEmployeeData.data = action.payload.data;
    },
    getEmployeeDetailsFailure(state, action: PayloadAction<string | boolean>) {
      state.isLoading = false;
      state.errorEmployeeState = action.payload;
    },
    updateEmployeeDetailsRequest(
      state,
      action: PayloadAction<updateEmployeeDetailsParams>,
    ) {
      state.isLoading = true;
      state.errorEmployeeState = false;
    },
    updateEmployeeDetailsSuccess(
      state,
      action: PayloadAction<updateEmployeeDetailsSuccessResponse>,
    ) {
      state.isLoading = false;
      state.successEmployeeState = action.payload.status_code;
      state.selectedEmployeeData.data = action.payload.data;
    },
    updateEmployeeDetailsFailure(
      state,
      action: PayloadAction<string | boolean>,
    ) {
      state.isLoading = false;
      state.errorEmployeeState = action.payload;
    },
    resetCurrentSelectedEmployee(state){
      state.selectedEmployeeData = {
        id: null,
        data: null,
      }
    },  
    resetMessage(state) {
      state.errorEmployeeState = false;
      state.successEmployeeState = false;
    },
  },
});

export default EmployeeSlice.reducer;
export const {
  getEmployeesRequest,
  getEmployeesSuccess,
  getEmployeesFailure,
  addEmployeeRequest,
  addEmployeeSuccess,
  addEmployeeFailure,
  setCurrentSelectedEmployee,
  getEmployeeDetailsRequest,
  getEmployeeDetailsSuccess,
  getEmployeeDetailsFailure,
  updateEmployeeDetailsRequest,
  updateEmployeeDetailsSuccess,
  updateEmployeeDetailsFailure,
  resetMessage,
  resetCurrentSelectedEmployee,
} = EmployeeSlice.actions;
